import { useEffect, useState } from "react";
import { UPCOMING } from "../lib/links";
import { useReveal } from "../hooks/useReveal";
import LetterReveal from "./LetterReveal";
import Bicycle from "./Bicycle";

function useCountdown(targetISO) {
  const [diff, setDiff] = useState(() => Math.max(0, new Date(targetISO) - new Date()));
  useEffect(() => {
    const t = setInterval(() => {
      setDiff(Math.max(0, new Date(targetISO) - new Date()));
    }, 1000);
    return () => clearInterval(t);
  }, [targetISO]);
  const totalSec = Math.floor(diff / 1000);
  return {
    days: Math.floor(totalSec / 86400),
    hours: Math.floor((totalSec % 86400) / 3600),
    minutes: Math.floor((totalSec % 3600) / 60),
    seconds: totalSec % 60,
  };
}

const TARGET_ISO = "2026-06-22T00:00:00Z";

export default function UpcomingRelease() {
  const { ref, visible } = useReveal();
  const { days, hours, minutes, seconds } = useCountdown(TARGET_ISO);

  const presave = () => {
    window.open(UPCOMING.presave_url, "_blank", "noopener,noreferrer");
  };

  return (
    <section
      id="presave"
      data-testid="upcoming-section"
      ref={ref}
      className={`relative bg-white py-28 md:py-40 px-6 md:px-12 lg:px-20 reveal ${
        visible ? "in-view" : ""
      }`}
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="flex items-center gap-4 mb-16">
          <div className="overline" data-testid="upcoming-overline">pre-save</div>
          <div className="h-px flex-1 bg-[#E6EAEF]" />
          <div className="font-mono-meta text-[10px] tracking-[0.3em] uppercase text-[#8C8C8C]">
            {UPCOMING.release_date}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          {/* Cover */}
          <div className="lg:col-span-6">
            <div
              data-testid="upcoming-artwork"
              className="relative aspect-square overflow-hidden border border-[#E6EAEF] bg-[#FAFBFC]"
            >
              <img
                src={UPCOMING.cover}
                alt={`${UPCOMING.title} cover artwork`}
                className="w-full h-full object-cover transition-transform duration-[1500ms] hover:scale-[1.02]"
              />
            </div>
          </div>

          {/* Details */}
          <div className="lg:col-span-6">
            <div className="font-mono-meta text-[10px] tracking-[0.32em] uppercase text-[#6B8FAE] mb-4">
              new single · coming soon
            </div>

            <div className="relative inline-block">
              <h2
                data-testid="upcoming-title"
                className="font-serif-display text-6xl sm:text-7xl md:text-8xl leading-[0.95] tracking-tight text-[#0A0A0A]"
              >
                <LetterReveal text="Be Alright" italicWords={["Be"]} stepMs={60} />
              </h2>
              {/* Bicycle orbiting the title */}
              <div className="bike-orbit hidden md:block" aria-hidden>
                <div className="bike-rider">
                  <Bicycle className="w-full h-full" />
                </div>
              </div>
            </div>

            <p className="mt-8 text-lg md:text-xl text-[#595959] leading-relaxed max-w-md">
              Out June 22, 2026. Pre-save now to add it to your library the moment it drops.
            </p>

            {/* Countdown — minimal */}
            <div
              data-testid="countdown"
              className="mt-12 grid grid-cols-4 gap-4 max-w-md"
            >
              {[
                { label: "days", value: days },
                { label: "hrs", value: hours },
                { label: "min", value: minutes },
                { label: "sec", value: seconds },
              ].map((u) => (
                <div key={u.label} className="text-center border-l border-[#E6EAEF] first:border-l-0 pl-4 first:pl-0">
                  <div className="font-serif-display text-3xl md:text-4xl text-[#0A0A0A] leading-none tracking-tight">
                    {String(u.value).padStart(2, "0")}
                  </div>
                  <div className="mt-2 font-mono-meta text-[9px] tracking-[0.3em] uppercase text-[#8C8C8C]">
                    {u.label}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-5">
              <button
                data-testid="presave-button"
                onClick={presave}
                className="btn-minimal"
              >
                Pre-Save
              </button>
              <a
                href={UPCOMING.presave_url}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="presave-link"
                className="font-mono-meta text-[10px] tracking-[0.3em] uppercase text-[#595959] hover:text-[#6B8FAE] link-underline"
              >
                lnk.to/bealrightsylasever →
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
